/**
 * MessageArea Component
 * Displays AI conversation messages
 */

import React, { useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Paper,
  Divider,
  CircularProgress,
  Chip,
  Tooltip
} from '@mui/material';
import {
  Person as UserIcon,
  SmartToy as AIIcon,
  Schedule as TimeIcon,
  Speed as PerformanceIcon,
  Token as TokenIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { AIMessage, ConversationWithMessages, ConversationStats } from '../../services/AIConsoleService';
import { format } from 'date-fns';

const MessageContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  height: '100%',
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column'
}));

const MessageBubble = styled(Paper)<{ role: 'user' | 'assistant' | 'system' }>(({ theme, role }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  maxWidth: '85%',
  wordBreak: 'break-word',
  ...(role === 'user' && {
    marginLeft: 'auto',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText
  }),
  ...(role === 'assistant' && {
    marginRight: 'auto',
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.text.primary
  }),
  ...(role === 'system' && {
    marginLeft: 'auto',
    marginRight: 'auto',
    backgroundColor: theme.palette.info.light,
    color: theme.palette.info.contrastText,
    textAlign: 'center'
  })
}));

const MessageHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.default
}));

const StatsBar = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  display: 'flex',
  gap: theme.spacing(2),
  alignItems: 'center',
  flexWrap: 'wrap'
}));

interface MessageAreaProps {
  conversation: ConversationWithMessages;
  messages: AIMessage[];
  stats?: ConversationStats | null;
  loading?: boolean;
}

const MessageArea: React.FC<MessageAreaProps> = ({
  conversation,
  messages,
  stats,
  loading = false
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const renderMessageContent = (message: AIMessage) => {
    // Split content by newlines and render with proper formatting
    const lines = message.content.split('\n');

    return lines.map((line, index) => (
      <Typography
        key={index}
        variant="body2"
        component="div"
        sx={{
          whiteSpace: 'pre-wrap',
          mb: index < lines.length - 1 ? 0.5 : 0
        }}
      >
        {line || '\u00A0'} {/* Non-breaking space for empty lines */}
      </Typography>
    ));
  };

  const renderMessageMetadata = (message: AIMessage) => {
    if (message.role === 'system') return null;

    return (
      <Box display="flex" alignItems="center" gap={1} mt={1} sx={{ opacity: 0.7 }}>
        <Typography variant="caption">
          {format(new Date(message.created_at), 'HH:mm')}
        </Typography>
        {message.role === 'assistant' && message.model_used && (
          <>
            <Typography variant="caption">•</Typography>
            <Typography variant="caption">{message.model_used}</Typography>
          </>
        )}
        {message.role === 'assistant' && message.processing_time_ms && (
          <>
            <Typography variant="caption">•</Typography>
            <Typography variant="caption">{message.processing_time_ms}ms</Typography>
          </>
        )}
        {message.role === 'assistant' && message.tokens_used && (
          <>
            <Typography variant="caption">•</Typography>
            <Typography variant="caption">{message.tokens_used} tokens</Typography>
          </>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <MessageHeader>
        <Typography variant="h6" gutterBottom>
          {conversation.title}
        </Typography>
        <Box display="flex" gap={1} alignItems="center">
          <Chip
            label={conversation.context_type}
            size="small"
            color={conversation.context_type === 'general' ? 'default' : 'primary'}
          />
          {conversation.project && (
            <Chip
              label={conversation.project.name}
              size="small"
              variant="outlined"
            />
          )}
        </Box>
      </MessageHeader>

      {stats && (
        <StatsBar>
          <Tooltip title="Total Messages">
            <Chip
              icon={<AIIcon />}
              label={`${stats.total_messages} messages`}
              size="small"
              variant="outlined"
            />
          </Tooltip>

          {stats.total_tokens > 0 && (
            <Tooltip title="Total Tokens Used">
              <Chip
                icon={<TokenIcon />}
                label={`${stats.total_tokens.toLocaleString()} tokens`}
                size="small"
                variant="outlined"
              />
            </Tooltip>
          )}

          {stats.avg_response_time_ms > 0 && (
            <Tooltip title="Average Response Time">
              <Chip
                icon={<PerformanceIcon />}
                label={`${Math.round(stats.avg_response_time_ms)}ms avg`}
                size="small"
                variant="outlined"
              />
            </Tooltip>
          )}

          <Tooltip title="Last Message">
            <Chip
              icon={<TimeIcon />}
              label={format(new Date(stats.last_message_at), 'MMM d, HH:mm')}
              size="small"
              variant="outlined"
            />
          </Tooltip>
        </StatsBar>
      )}

      <MessageContainer ref={containerRef} data-testid="message-area">
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" sx={{ flex: 1 }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Loading messages...
            </Typography>
          </Box>
        ) : (
          <>
            {messages.map((message, index) => (
              <Box key={message.id} display="flex" alignItems="flex-start" gap={1}>
                {message.role !== 'system' && (
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      mt: 1,
                      ...(message.role === 'user' && { order: 2, ml: 'auto' })
                    }}
                  >
                    {message.role === 'user' ? <UserIcon /> : <AIIcon />}
                  </Avatar>
                )}

                <MessageBubble
                  role={message.role}
                  elevation={1}
                  data-testid={`message-${message.id}`}
                  sx={{
                    ...(message.role === 'user' && { order: 1 })
                  }}
                >
                  {renderMessageContent(message)}
                  {renderMessageMetadata(message)}
                </MessageBubble>
              </Box>
            ))}

            {messages.length === 0 && (
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                sx={{ flex: 1, textAlign: 'center', p: 3 }}
              >
                <AIIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  Start a conversation
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Ask me anything about your project, technical questions, or estimations
                </Typography>
              </Box>
            )}

            <div ref={messagesEndRef} />
          </>
        )}
      </MessageContainer>
    </Box>
  );
};

export default MessageArea;
